import { useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { CheckIcon, XIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { H3, H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { List, ListItem } from "simplydo/react/ui/list";
import { Message } from "simplydo/react/ui/message";
import { Modal, ModalActions, ModalContent } from "simplydo/react/ui/modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";
import { TextArea } from "simplydo/react/ui/textarea";

import api from "api";
import util from "utils/utils";

import { UserChip } from "./Chips";
import { LargeCheckbox } from "./UI";

type MergeIdea = OpenAPI.Schemas["Idea"] & {
  owner?: OpenAPI.Schemas["User"];
  parent?: string;
  children?: string[];
};

export type MergeModalProps = {
  ideas: MergeIdea[];
  challenge: OpenAPI.Schemas["Challenge"];
  projectLanes: OpenAPI.Schemas["ProjectLane"][];
  show?: boolean;
  proceed?: () => void;
  cancel?: () => void;
  dismiss?: () => void;
};

const IdeaTableRow = ({
  idea,
  onChangeCheckbox,
  checked,
}: {
  idea: MergeIdea;
  onChangeCheckbox?: () => void;
  checked?: boolean;
}) => {
  const theme = useTheme();
  return (
    <TableRow key={idea._id}>
      {onChangeCheckbox ? (
        <TableCell collapsing>
          <LargeCheckbox checked={!!checked} onChange={onChangeCheckbox} />
        </TableCell>
      ) : null}
      <TableCell collapsing>
        <ImageWithFallback
          src={util.ideaCoverImage(idea, "200x200")}
          fallbackSrc={util.ideaCoverImage(undefined)}
          avatar
        />
      </TableCell>
      <TableCell collapsing={!theme.sizes.isComputer}>
        <span
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {idea.name}
        </span>
      </TableCell>
      <TableCell collapsing={!theme.sizes.isComputer}>
        <UserChip user={idea.owner} />
      </TableCell>
    </TableRow>
  );
};

export const MergeModal = ({
  show,
  proceed,
  dismiss,
  cancel,
  ideas,
  projectLanes,
  challenge,
}: MergeModalProps) => {
  const theme = useTheme();
  const firstIdea = ideas[0];
  const [selectedOwner, setSelectedOwner] = useState<MergeIdea | undefined>(
    firstIdea,
  );
  const [name, setName] = useState(firstIdea?.name ?? "");
  const [projectLane, setProjectLane] = useState("defaultLane");
  const [mergeJustification, setMergeJustification] = useState("");
  const { t } = useTranslation();

  const nonMergedIdeas = useMemo(
    () => ideas.filter((i) => !(i.children || i.parent)),
    [ideas],
  );
  const mergeParent = useMemo(() => ideas.find((i) => i.children), [ideas]);
  const isExistingMerge = useMemo(
    () =>
      ideas.filter((i) => i.children).length === 1 &&
      nonMergedIdeas.length === ideas.length - 1,
    [ideas, nonMergedIdeas.length],
  );

  return (
    <Modal open={show} onClose={dismiss} size="small">
      <H3>{`Merge ${ideas.length} ${t("generic.ideas")}`}</H3>
      <ModalContent scrolling>
        <H5>{`${t("common:capitalise", { key: "generic.ideas" })} to be merged`}</H5>
        {!isExistingMerge ? (
          <>
            <span>{`The following ${t("generic.ideas")} will be included in the merge.`}</span>
            <span>
              {`Please choose the ${t("generic.idea")} owner and cover image you wish to use for the new merged ${t("generic.idea")}. Note that the
              authors from the other ${t("generic.ideas")} will be added as authors to the new merged ${t("generic.idea")}.`}
            </span>
          </>
        ) : null}
        <Message color="blue" size="small">
          {challenge?.ideaVisibility === "users"
            ? `${t("common:capitalise", { key: "generic.idea" })} visibility is currently set to "Users". Authors of the ${t("generic.ideas")} you merge will be added as authors to the merged ${t("generic.idea")}. They will be able to see the merged ${t("generic.idea")}, and if ${t("generic.idea")} visibility is set to "Users", they will be able to see the other ${t("generic.ideas")} in the merge.`
            : `${t("common:capitalise", { key: "generic.idea" })} visibility is currently set to "${t("common:capitalise", { key: "generic.challenge" })} Managers". Authors of the ${t("generic.ideas")} you merge will not be added as authors to the merged ${t("generic.idea")}. They will not be able to see the merged ${t("generic.idea")}, and if ${t("generic.idea")} visibility is set to "Users", they will not be able to see the other ${t("generic.ideas")} in the merge.`}
        </Message>
        {isExistingMerge && mergeParent ? (
          <>
            <Divider horizontal>
              The following{" "}
              {util.pluralise(
                nonMergedIdeas.length,
                `${t("generic.idea")}`,
                `${t("generic.ideas")}`,
              )}
              ...
            </Divider>
            <Table unstackable>
              <TableBody>
                {nonMergedIdeas.map((idea) => (
                  <IdeaTableRow key={idea._id} idea={idea} />
                ))}
              </TableBody>
            </Table>
            <Divider horizontal>Will be merged into...</Divider>
            <Table unstackable>
              <TableBody>
                <IdeaTableRow key={mergeParent._id} idea={mergeParent} />
              </TableBody>
            </Table>
          </>
        ) : (
          <>
            <Table unstackable>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell width="1"></TableHeaderCell>
                  <TableHeaderCell width="1"></TableHeaderCell>
                  <TableHeaderCell width="6">Name</TableHeaderCell>
                  <TableHeaderCell width="4">User</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ideas.map((idea) => (
                  <IdeaTableRow
                    key={idea._id}
                    idea={idea}
                    checked={selectedOwner?._id === idea._id}
                    onChangeCheckbox={() => {
                      if (name === selectedOwner?.name) {
                        setName(idea.name ?? "");
                      }
                      setSelectedOwner(idea);
                    }}
                  />
                ))}
              </TableBody>
            </Table>
            <H5>{t("common:capitalise", { key: "generic.idea" })} details</H5>
            <span>
              Please choose a name and project lane (if appropriate) for the
              newly-merged {t("generic.idea")}.
            </span>
            <div style={{ marginTop: 10 }}>
              <FormField>
                <FormLabel>
                  {`${t("common:capitalise", { key: "generic.idea" })} name`}
                </FormLabel>
                <Input
                  fluid
                  value={name}
                  onChange={(_e, data) => {
                    setName(`${data.value ?? ""}`);
                  }}
                />
              </FormField>
              {projectLanes ? (
                <FormField>
                  <FormLabel>Lane</FormLabel>
                  <Dropdown
                    variant="outline"
                    value={projectLane}
                    onChange={(_e, s) => {
                      setProjectLane(`${s.value ?? "defaultLane"}`);
                    }}
                    options={[
                      {
                        key: "defaultLane",
                        text: "Default lane",
                        value: "defaultLane",
                      },
                      ...projectLanes.map((lane) => ({
                        key: lane._id,
                        text: lane.name,
                        value: lane._id,
                      })),
                    ]}
                  />
                </FormField>
              ) : null}
            </div>
          </>
        )}
        <H5>Considerations</H5>
        <List variant="bulleted" compact>
          <ListItem>
            Merged {t("generic.ideas")} can be un-merged at any time. The{" "}
            {t("generic.ideas")} included will not be changed.
          </ListItem>
          <ListItem>
            The individual {t("generic.ideas")} will no longer appear in the{" "}
            {t("generic.idea")} list. Instead, the &quot;merged&quot;{" "}
            {t("generic.idea")} will be displayed.
          </ListItem>
          <ListItem>
            Depending on your {t("generic.challenge")} settings, users with{" "}
            {t("generic.ideas")} in the merge may be able to see the other users
            and {t("generic.ideas")}.
          </ListItem>
        </List>
        <FormField>
          <FormLabel>
            <b>Justify this merge</b>
          </FormLabel>
          <TextArea
            value={mergeJustification}
            onChange={(_e, { value }) => setMergeJustification(value as string)}
            placeholder={`I am merging the ${t("generic.ideas")} ${ideas.map((idea) => idea.name).join(", ")} because...`}
          />
        </FormField>
      </ModalContent>
      <ModalActions>
        <Button onClick={cancel} variant="outline">
          <Icon icon={XIcon} /> Cancel
        </Button>
        <Button
          onClick={() => {
            if (isExistingMerge && mergeParent) {
              api.ideas.updateMergedIdea(
                mergeParent._id,
                { ideaIds: nonMergedIdeas.map((idea) => idea._id) },
                () => {
                  toast.success(
                    `Added ${util.pluralise(nonMergedIdeas.length, `${t("generic.idea")}`, `${t("generic.ideas")}`)} to ${mergeParent.name}`,
                  );
                  proceed?.();
                },
                () => {
                  toast.error(`Failed to merge ${t("generic.ideas")}`);
                },
              );
            } else if (firstIdea && selectedOwner) {
              const mergeData: OpenAPI.POST<"/ideas/merge">["requestBody"] = {
                ideaIds: ideas.map((idea) => idea._id),
                challengeId: firstIdea.challenge,
                ownerIdeaId: selectedOwner._id,
                name,
              };
              if (projectLane !== "none") {
                mergeData.projectLane = projectLane;
              }
              api.ideas.merge(
                mergeData,
                () => {
                  toast.success(`Successfully merged ${t("generic.ideas")}`);
                  proceed?.();
                },
                () => {
                  toast.error(`Failed to merge ${t("generic.ideas")}`);
                },
              );
            }
          }}
          disabled={!name}
          color="primary"
        >
          <Icon icon={CheckIcon} /> Merge {t("generic.ideas")}
        </Button>
      </ModalActions>
    </Modal>
  );
};
